/**
 * Customer-facing display (pos-customer-display) — a DECOUPLED, menu-less,
 * READ-ONLY second screen at the till. Served standalone at
 * ``/pos/display?token=<signed station token>`` (no admin shell, no login): a
 * dumb browser pointed at the URL. It mirrors the active order as it is
 * assembled + the total, and during a card tender shows the SumUp status + a
 * scan-to-pay QR. It can never mutate anything.
 *
 * Wears the Wanderzirkus circus-poster design (Tally POS Design System, project
 * 4faf9d8e): oversized Anton totals read from metres away, a persistent brand
 * rail with the crew badge, the QR on a white panel. Styling lives in
 * styles/pos-theme/pos-display.css (.cd-*). Driven from ``usePosDisplay``.
 */

import { QRCode, Result, Spin } from "antd";
import { useSearchParams } from "react-router-dom";

import type { PosDisplayCard, PosDisplayState } from "../../api/client";

/** A renderable order line (the live feed line, or a grouped summary line). */
type DisplayLine = {
  name: string;
  qty: number;
  line_total_cents: number;
  parent_line_id?: number | null;
};

/** Customer-facing line name. A deposit carries its direction in the qty/total
 * sign — surface it as Bottle in / Bottle out (matching the operator screen),
 * not the backend's bare "Pfand". */
function lineName(l: PosDisplayState["lines"][number]): string {
  if (l.kind === "deposit") return l.qty < 0 ? "Bottle in" : "Bottle out";
  return l.name;
}

/** Sum the feed into one summary line per name — shown while settling. Bottles
 * group by DIRECTION (all outs together, all ins together) so the guest sees
 * "Bottle out ×3" / "Bottle in ×2"; counts read as a magnitude, the amount
 * keeps its sign (an in / return is negative). */
function groupLines(lines: PosDisplayState["lines"]): DisplayLine[] {
  const m = new Map<string, DisplayLine>();
  for (const l of lines) {
    const name = lineName(l);
    const g = m.get(name) ?? { name, qty: 0, line_total_cents: 0 };
    g.qty += Math.abs(l.qty);
    g.line_total_cents += l.line_total_cents;
    m.set(name, g);
  }
  return [...m.values()];
}
import { FullscreenButton } from "../../components/pos/FullscreenButton";
import { PosThemeProvider } from "../../components/pos/PosThemeProvider";
import { POS_BADGE_SRC, POS_READER_SRC } from "../../components/pos/posBrand";
import { eur } from "../../components/pos/orderModel";
import { usePosDisplay } from "../../components/pos/usePosDisplay";

/** The crew brand wordmark shown beside the badge. */
const BRAND = "Wanderzirkus";

const Frame = ({ children }: { children: React.ReactNode }) => (
  <PosThemeProvider>
    <div className="pos-display-frame">
      {/* Unobtrusive corner control: the display is opened once on a monitor,
          then taken fullscreen with a tap. Requires a user gesture, so it's a
          button. */}
      <div className="pos-display-fs">
        <FullscreenButton type="text" size="large" />
      </div>
      {children}
    </div>
  </PosThemeProvider>
);

export const PosCustomerDisplayPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { state, error } = usePosDisplay(token);

  if (error && !state) {
    return (
      <Frame>
        <div className="cd-idle">
          <Result status="warning" title="Display unavailable" subTitle={error} />
        </div>
      </Frame>
    );
  }
  if (!state) {
    return (
      <Frame>
        <div className="cd-idle">
          <Spin size="large" />
        </div>
      </Frame>
    );
  }
  return <Frame>{renderState(state)}</Frame>;
};

function Brand({ stationName }: { stationName: string }) {
  return (
    <div className="cd-rail__brand">
      <img className="cd-rail__badge" src={POS_BADGE_SRC} alt={BRAND} />
      <span className="cd-rail__word">{BRAND}</span>
      <span className="cd-rail__station">{stationName} · Crew Bar</span>
    </div>
  );
}

function CardPay({ card }: { card: PosDisplayCard }) {
  if (card.status === "paid") {
    return <Result status="success" title="Payment received" />;
  }
  if (card.status === "declined") {
    return <Result status="error" title="Payment declined" />;
  }
  if (card.status === "expired") {
    return <Result status="warning" title="Payment expired" />;
  }
  // waiting
  return (
    <div className="cd-rail__pay">
      <img className="cd-rail__reader" src={POS_READER_SRC} alt="SumUp Solo reader" />
      {card.qr_url && (
        <div className="cd-rail__qr">
          <QRCode value={card.qr_url} size={184} bordered={false} />
        </div>
      )}
      <p className="cd-rail__prompt">
        {card.reader_armed && card.qr_url ? (
          <>
            Tap your card
            <br />— or scan to pay
          </>
        ) : card.reader_armed ? (
          "Tap your card on the reader"
        ) : (
          "Scan to pay"
        )}
      </p>
      <span className="cd-rail__statuspill">Waiting for payment</span>
    </div>
  );
}

function BrandRail({ s }: { s: PosDisplayState }) {
  const pay = s.state === "card";
  return (
    <aside className={`cd-rail${pay ? " cd-rail--pay" : ""}`}>
      <Brand stationName={s.station_name} />
      {pay && s.card ? (
        <CardPay card={s.card} />
      ) : (
        <p className="cd-rail__tag">
          Order at the bar —<br />
          cash, card or scan to pay
        </p>
      )}
    </aside>
  );
}

function OrderLines({ lines }: { lines: DisplayLine[] }) {
  // Passive display: no scroll, no interaction. The feed is bottom-anchored
  // (CSS) so the newest line is always visible as it fades in; older lines clip
  // off the top under a fade when the order outgrows the screen.
  return (
    <div className="cd-lines">
      {lines.map((l, i) => (
        <div
          className={`cd-line${l.parent_line_id != null ? " cd-line--bundled" : ""}`}
          key={i}
        >
          <span className="cd-line__name">
            {l.qty > 1 && <span className="cd-line__qty">{l.qty}× </span>}
            {l.name}
          </span>
          <span
            className={`cd-line__amt${l.line_total_cents < 0 ? " cd-line__amt--refund" : ""}`}
          >
            {eur(l.line_total_cents)}
          </span>
        </div>
      ))}
    </div>
  );
}

function OrderView({ s }: { s: PosDisplayState }) {
  // "Pay now" view for BOTH a card tender (QR, handled by the rail) and a
  // cash/comp tender (operator opened the popup → server "settling" state).
  const pay = s.state === "card" || s.state === "settling";
  // Building: the raw add-by-add feed (each tap appears + fades in), names
  // surfaced (Bottle in/out). Settling: collapse to one summary line per name
  // (drinks + bottles-by-direction grouped) so the guest reads the final bill.
  const lines: DisplayLine[] = pay
    ? groupLines(s.lines)
    : s.lines.map((l) => ({
        name: lineName(l),
        qty: l.qty,
        line_total_cents: l.line_total_cents,
        parent_line_id: l.parent_line_id,
      }));
  return (
    <div className="cd-order">
      <div className="cd-order__main">
        <span className="cd-order__eyebrow">{pay ? "Pay now" : "Your order"}</span>
        <OrderLines lines={lines} />
        <div className="cd-order__total">
          <span className="cd-total__label">{pay ? "Amount due" : "Total"}</span>
          <span className="cd-total__amt">{eur(s.total_cents)}</span>
        </div>
      </div>
      <BrandRail s={s} />
    </div>
  );
}

function renderState(s: PosDisplayState) {
  if (s.state === "idle") {
    return (
      <div className="cd-idle">
        <img className="cd-idle__badge" src={POS_BADGE_SRC} alt={BRAND} />
        <span className="cd-idle__word">{BRAND}</span>
        <span className="cd-idle__station">{s.station_name} · Crew Bar</span>
        <span className="cd-idle__welcome">Welcome</span>
        <span className="cd-idle__sub">
          Order at the bar — cash, card or scan to pay
        </span>
      </div>
    );
  }

  if (s.state === "settled") {
    const total = s.settled_total_cents ?? s.total_cents;
    return (
      <div className="cd-order">
        <div className="cd-order__main cd-order__main--center">
          <span className="cd-idle__word" style={{ fontSize: "var(--text-5xl)" }}>
            Danke!
          </span>
          {s.receipt_no && (
            <span className="cd-rail__station" style={{ marginTop: 8 }}>
              Receipt {s.receipt_no}
            </span>
          )}
          <div className="cd-settled__nums">
            <div>
              <span>Total</span>
              <strong>{eur(total)}</strong>
            </div>
            {s.change_due_cents != null && (
              <div>
                <span>Change</span>
                <strong className="cd-settled__change">
                  {eur(s.change_due_cents)}
                </strong>
              </div>
            )}
          </div>
        </div>
        <BrandRail s={s} />
      </div>
    );
  }

  // building / card → the order list + total + brand rail (card adds the pay rail)
  return <OrderView s={s} />;
}
